import Avatar, { type SizeType } from '@atlaskit/avatar';
import { token } from '@atlaskit/tokens';
import { getBaseUrl } from '@databases/database-state';
import EllipsisOverflow from '@databases/field-value/common/EllipsisOverflow';
import { type Group, isUser, type User } from '@databases/types/confluence';
import React, { type FunctionComponent, type ReactNode, useMemo } from 'react';
import styled from 'styled-components';

const GROUP_AVATAR_PATH = '/images/icons/avatar_group_48.png';

const Container = styled.div`
  display: flex;
  align-items: center;

  > :first-child {
    margin-right: ${token('space.050')};
  }
`;

interface AvatarWithLabelProps {
  userOrGroup: User | Group | undefined;
  label?: ReactNode;
  size?: SizeType;
  className?: string;
  avatarWrapper?: (avatar: ReactNode) => ReactNode;
  avatarOnly?: boolean;
}

export const AvatarWithLabel: FunctionComponent<AvatarWithLabelProps> = ({
  userOrGroup,
  label,
  size = 'small',
  className,
  avatarWrapper,
  avatarOnly = false
}) => {
  const displayName = !userOrGroup
    ? ''
    : isUser(userOrGroup)
      ? userOrGroup.displayName
      : userOrGroup.name;

  const avatarSrc = useMemo(() => {
    if (!userOrGroup) {
      return undefined;
    }

    const baseUrl = getBaseUrl();
    return isUser(userOrGroup)
      ? new URL(userOrGroup.profilePicture.path, baseUrl).toString()
      : `${baseUrl}${GROUP_AVATAR_PATH}`;
  }, [userOrGroup]);

  const avatar = (
    <Avatar
      testId="avatar-with-label"
      appearance="circle"
      size={size}
      src={avatarSrc}
      borderColor="none"
      name={label != null ? displayName : undefined}
    />
  );

  if (avatarOnly) {
    return <>{avatarWrapper ? avatarWrapper(avatar) : avatar}</>;
  }

  return (
    <Container className={className}>
      {avatarWrapper ? avatarWrapper(avatar) : avatar}
      <EllipsisOverflow>{label ?? displayName}</EllipsisOverflow>
    </Container>
  );
};
